import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiResponse } from '../models';
import {
  RoleFeatures,
  ScreenFeatureGroups,
} from '../models/Role-Permission.model';
import { apiPath } from '../constants/api-routes';
import { BehaviorSubject, map } from 'rxjs';
import { ScreenType } from '../enums/screen.type';
import { AuthService } from './auth.service';
import { constants } from '../constants';

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private http = inject(HttpClient);
  private permissionsSubject: BehaviorSubject<RoleFeatures | null> =
    new BehaviorSubject<RoleFeatures | null>(null);
  private screenPermissionsSubject: BehaviorSubject<ScreenFeatureGroups | null> =
    new BehaviorSubject<ScreenFeatureGroups | null>(this.getItem('screen')!);
  private authService = inject(AuthService);

  constructor() {}

  fetchPermission() {
    return this.http
      .get<ApiResponse<RoleFeatures>>(apiPath.roles.fetchFeaturesOfUser)
      .pipe(
        map((v) => {
          this.setPermissions(v.data);
          return v;
        })
      );
  }

  getItem(feature?: 'screen'): any {
    if (feature) {
      return JSON.parse(
        sessionStorage.getItem(constants.storageKeys.screenPermission)!
      );
    }
    const permission = this.permissionsSubject.value;
    return permission;
  }

  getPermission() {
    return this.permissionsSubject.asObservable();
  }

  getScreenPermissionsData() {
    return this.screenPermissionsSubject.value;
  }

  getPermissionsofRole(roleName: string) {
    const permissions = this.permissionsSubject.value;
    if (permissions) return permissions[roleName];
    return null;
  }

  setPermissions(permission: RoleFeatures) {
    this.permissionsSubject.next(permission);
    const roles = Object.keys(permission);
    const screenPermissions: ScreenFeatureGroups = {
      'activity logs': false,
      'batch report': false,
      'client management': false,
      calldialer: false,
      batches: false,
      'csv field mapper': false,
      dashboard: false,
      'features management': false,
      'role management': false,
    };
    roles.forEach((r) => {
      Object.keys(permission[r]).forEach((s) => {
        (screenPermissions as any)[s] = this.hasPermissionToAnyone(s as any);
      });
    });
    screenPermissions.batches = this.hasPermissionToAnyone('batches' as any);
    sessionStorage.setItem(
      constants.storageKeys.screenPermission,
      JSON.stringify(screenPermissions)
    );
    this.screenPermissionsSubject.next(screenPermissions);
  }

  hasPermissionByClient(
    clientId: string,
    screen: ScreenType,
    key: string,
    roleName?: string
  ): boolean {
    var client;
    if (!roleName) {
      client = this.authService
        .getProperty('data')
        .filter((d: any) => clientId === d.clientId[0]);
    }

    const role = client && client.length ? client[0].roleName : roleName;

    if (!role) return false;

    const permissions = this.getPermissionsofRole(role);

    if (!permissions) return false;
    const screenPermissions = permissions[screen];

    if (screenPermissions) {
      return (screenPermissions as any)[key];
    }

    return false;
  }

  hasPermissionByRole(
    roleName: string,
    screen: ScreenType,
    key: string
  ): boolean {
    const client = this.authService
      .getProperty('data')
      .filter((d: any) => roleName === d.roleName);

    if (!client.length) return false;

    const permissions = this.getPermissionsofRole(roleName);
    if (!permissions) return false;

    const screenPermissions = (permissions as any)[screen];

    if (screenPermissions) {
      return (screenPermissions as any)[key];
    }

    return false;
  }

  hasScreenPermissionByRole(roleName: string, screenName: ScreenType) {
    const screens = this.getPermissionsofRole(roleName);
    if (screens) {
      const screenPermissions = (screens as any)[screenName.toLowerCase()];
      if (!screenPermissions) return false;
      const has = Object.keys(screenPermissions).some(
        (s) => screenPermissions[s]
      );
      return has;
    }

    return false;
  }

  hasPermissionToAnyone(screenName: ScreenType, key?: string): boolean {
    const permissions = this.permissionsSubject.value;
    if (!permissions) return false;
    const roles = Object.keys(permissions!);
    if (key) {
      return roles.some((r) => this.hasPermissionByRole(r, screenName, key));
    }

    return roles.some((r) => this.hasScreenPermissionByRole(r, screenName));
  }
}
